import { create } from 'zustand';
import { DateRange } from 'react-day-picker';
import { ColumnState } from 'ag-grid-community';

import { defaultWMS2060_R01Dto, WMS2060_R01Dto } from './Dto_WMS2060_R01';
import { formatDateMonth, formatDateYear4 } from 'src/utils/string/stringUtil';
import { showWarning } from 'src/utils/toast/toastUtil';

export interface WMS2060_R01Filter {
    logicCenter: string;
    warehouseCd: string;
    itemCd: string;
	itemNm: string;
    brandCd: string;
}

export interface Store_WMS2060_R01 {
	WMS2060_R01Filter: WMS2060_R01Filter;
	WMS2060_R01PastFilter: WMS2060_R01Filter;
	setWMS2060_R01Filter: (filter: Partial<WMS2060_R01Filter>) => void;
	setWMS2060_R01PastFilter: (filter: Partial<WMS2060_R01Filter>) => void;

	WMS2060_R01List: WMS2060_R01Dto[];
	setWMS2060_R01List: (WMS2060_R01List: WMS2060_R01Dto[]) => void;
	insertWMS2060_R01List: (data: WMS2060_R01Dto[], index?: number) => void;
	removeWMS2060_R01List: (index: number) => void;
	updateWMS2060_R01List: (index: number, WMS2060_R01List: WMS2060_R01Dto) => void;

	deletedWMS2060_R01List: string[];
	addDeletedWMS2060_R01List: (idList: string[]) => void;
	removeDeletedWMS2060_R01List: (idList?: string[]) => void;

	selectedWMS2060_R01List: string[];
	setSelectedWMS2060_R01List: (idList: string[]) => void;
	addSelectedWMS2060_R01List: (idList: string[]) => void;
	removeSelectedWMS2060_R01List: (idList?: string[]) => void;
	initSelectedWMS2060_R01List: () => void;
	allSelectedWMS2060_R01List: () => void;

	originalWMS2060_R01List: WMS2060_R01Dto[];
	setOriginalWMS2060_R01List: (list: WMS2060_R01Dto[]) => void;

	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;

	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;

	allCount: number;					setAllCount: (allCount: number) => void;
	

	initData: () => void;

}

export const defaultWMS2060_R01Filter: WMS2060_R01Filter = {
	logicCenter: '',
	warehouseCd: '',
	itemCd: '',
	itemNm: '',
	brandCd: '',
};

export const useWMS2060_R01Store = create<Store_WMS2060_R01>((set) => ({
	WMS2060_R01Filter: defaultWMS2060_R01Filter,
	WMS2060_R01PastFilter: defaultWMS2060_R01Filter,
	setWMS2060_R01Filter: (update) => set((state) => ({
		WMS2060_R01Filter: {
			...state.WMS2060_R01Filter,
			...update
		}
	})),
	setWMS2060_R01PastFilter: (update) => set((state) => ({
		WMS2060_R01PastFilter: {
			...state.WMS2060_R01PastFilter,
			...update
		}
	})),

	WMS2060_R01List: [],
	setWMS2060_R01List: (WMS2060_R01List: WMS2060_R01Dto[]) => set((state) => {
		let newList: WMS2060_R01Dto[] = [];
		let valueAndOrderIndex = 1;
		WMS2060_R01List.forEach((item, index) => {
			// console.log(index + '번째 행의 item.Index 정보: ' 
			// 	+ ' valueIndex : ' + item.valueIndex 
			// 	+ ' valueAndOrderIndex : ' + valueAndOrderIndex
			// 	+ ' orderIndex : ' + item.orderIndex 
			// 	+ ' orderIndexSeq : ' + item.orderIndexSeq);
			newList.push({ 
				...defaultWMS2060_R01Dto,
				...item,
				valueIndex: item.valueIndex > 0 ? item.valueIndex : valueAndOrderIndex,
				orderIndex: item.orderIndex > -1 ? item.orderIndex : valueAndOrderIndex,
				orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0
			});
			valueAndOrderIndex++;
		});
		return {
			...state,
			WMS2060_R01List: newList
		}
	}),
	insertWMS2060_R01List: (data: WMS2060_R01Dto[], index?: number) => set((state) => {
		let newWMS2060_R01List = [...state.WMS2060_R01List];

		let targetRowData = newWMS2060_R01List.find(item => item.valueIndex === index);
		let targetOrderIndex = targetRowData?.orderIndex || 0;
		// 데이터에 orderIndex가 있으면 그것을 사용하고, 없으면 1 (index 파라미터는 배열 위치로만 사용)		

		const newOrderIndexSeq = (targetRowData?.orderIndexSeq || 0) + 1;
		// console.log('targetOrderIndex : ', targetOrderIndex);
		// console.log('newOrderIndexSeq : ', newOrderIndexSeq);
		let maxValueIndex = Math.max(0, ...newWMS2060_R01List.map(item => item.valueIndex || 0)) + 1;

		// newSYS4100_R01List에서 targetOrderIndex인 행들을 빼내서 newOrderIndexSeq 기준으로 다시 정렬
		// newOrderIndexSeq 보다 orderIndexSeq가 작은 행들은 유지. 
		// newOrderIndexSeq 보다 orderIndexSeq가 큰 행들은 기존 orderIndexSeq 값에 1을 더해서 저장.
		const targetRows = newWMS2060_R01List.filter(item => item.orderIndex === targetOrderIndex);
		targetRows.forEach(item => {
			if(item.orderIndexSeq < newOrderIndexSeq) {
				item.orderIndexSeq = item.orderIndexSeq;
			} else if(item.orderIndexSeq >= newOrderIndexSeq) {
				item.orderIndexSeq = item.orderIndexSeq + 1;
			}
		});

		// targetRows를 newSYS4100_R01List에 대체
		newWMS2060_R01List = newWMS2060_R01List.filter(item => item.orderIndex !== targetOrderIndex);
		newWMS2060_R01List.push(...targetRows);
		
		//새 행을 생성해서 newOrderIndexSeq 값의 행을 저장
		const newData = data.map(item => ({ ...defaultWMS2060_R01Dto, ...item, 
			valueIndex: maxValueIndex++,
			orderIndex: targetOrderIndex,
			orderIndexSeq: newOrderIndexSeq
		}));
		if (index !== undefined) {
			newWMS2060_R01List.splice(index, 0, ...newData);
		} else {
			newWMS2060_R01List.push(...newData);
		}

		return {
			...state,
			WMS2060_R01List: newWMS2060_R01List
		}
	}),
	removeWMS2060_R01List: (index: number) => set((state) => {
		const newWMS2060_R01List = [...state.WMS2060_R01List].filter((item) => item.valueIndex !== index);
		return {
			...state,
			WMS2060_R01List: newWMS2060_R01List
		}
	}),
	updateWMS2060_R01List: (valueIndex: number, data: WMS2060_R01Dto) => set((state) => {
		const _WMS2060_R01List = state.WMS2060_R01List.map(item =>
			item.valueIndex === valueIndex ? data : item
		).map(item => ({ ...item, movedLotQty: (item.lotQty * (-1)) + item.updateLotQty }));
		return {
			...state,
			WMS2060_R01List: _WMS2060_R01List,
		}
	}),


	deletedWMS2060_R01List: [],
	addDeletedWMS2060_R01List: (idList: string[]) => set((state) => {
		const _deletedWMS2060_R01List = [...state.deletedWMS2060_R01List];

		for(const valueIndex of idList) {
			const isInserted = state.WMS2060_R01List.find(item => item.valueIndex === Number(valueIndex) && item.inserted);
			const isModified = state.WMS2060_R01List.find(item => item.valueIndex === Number(valueIndex) && item.isModified);
			if(!isInserted && !isModified) {
				_deletedWMS2060_R01List.push(valueIndex);
			}
		}
		return {
			...state,
			deletedWMS2060_R01List: _deletedWMS2060_R01List
		}
	}),
	removeDeletedWMS2060_R01List: (idList?: string[]) => set((state) => ({
		...state,
		deletedWMS2060_R01List: [...state.deletedWMS2060_R01List].filter(value => idList ? !idList.includes(value) : false)
	})),

	selectedWMS2060_R01List: [],
	setSelectedWMS2060_R01List: (idList: string[]) => set({ selectedWMS2060_R01List: idList }),
	addSelectedWMS2060_R01List: (idList: string[]) => set((state) => {
		const _selectedWMS2060_R01List = [...state.selectedWMS2060_R01List];
		if(idList.length > 0) {
			_selectedWMS2060_R01List.push(...idList);
		}
		return {
			...state,
			selectedWMS2060_R01List: _selectedWMS2060_R01List
		}
	}),
	removeSelectedWMS2060_R01List: (idList?: string[]) => set((state) => ({
		...state,
		selectedWMS2060_R01List: [...state.selectedWMS2060_R01List].filter(value => idList ? !idList.includes(value) : false)
	})),
	initSelectedWMS2060_R01List: () => set({ selectedWMS2060_R01List: [] }),
	allSelectedWMS2060_R01List: () => set((state) => {
		const _selectedWMS2060_R01List = [...state.WMS2060_R01List.map(item => item.valueIndex.toString())];
		return {
			...state,
			selectedWMS2060_R01List: _selectedWMS2060_R01List
		}
	}),

	originalWMS2060_R01List: [],
	setOriginalWMS2060_R01List: (WMS2060_R01List: WMS2060_R01Dto[]) => set((state) => {
		let newList: WMS2060_R01Dto[] = [];
		let valueAndOrderIndex = 1;
		WMS2060_R01List.forEach((item, index) => {
			newList.push({ 
				...defaultWMS2060_R01Dto,
				...item,
				valueIndex: valueAndOrderIndex,
				orderIndex: valueAndOrderIndex,
				orderIndexSeq: valueAndOrderIndex
			});
			valueAndOrderIndex++;
		});
		return {
			...state,
			originalWMS2060_R01List: newList
		}
	}),

	columnState: null,
	setColumnState: (state: ColumnState[] | null) => set({ columnState: state }),

	scrollTop: null,
	setScrollTop: (top: number | null) => set({ scrollTop: top }),

	allCount: 0,					setAllCount: (allCount: number) => set({ allCount }),
						

	initData: () => set((state) => ({
		...state,
		WMS2060_R01Filter: defaultWMS2060_R01Filter,
		WMS2060_R01PastFilter: defaultWMS2060_R01Filter,
		WMS2060_R01List: [],
		selectedWMS2060_R01List: [],
		deletedWMS2060_R01List: [],
		allCount: 0,
		scrollTop: null,
	})),

}));